import { queryOptions } from "@tanstack/react-query";
import { api } from "../http";

export type AlertsListParams = {
  page?: number;
  per_page?: number;
  state?: string;
  alert_type?: string;
  search?: string;
  start_date?: string;
  end_date?: string;
};

export type PiiEntityInAlert = {
  entity_type: string;
  entity_text: string;
  start: number;
  end: number;
  score: number;
};

export type AiContentScore = {
  threshold: number;
  ai_content_score: number;
};

export type VoiceAuthScore = {
  threshold: number;
  voice_auth_score: number;
};

export type AlertMeeting = {
  id: string;
  name: string;
};

export type AlertParticipant = {
  id: string;
  name: string;
};

export type AlertItem = {
  id: string;
  utterance_id: string;
  alert_types: Array<string> | null;
  priority: string;
  state: string;
  pii_entities: Array<PiiEntityInAlert> | null;
  ai_content_score: AiContentScore | null;
  voice_auth_score: VoiceAuthScore | null;
  detected_at: string;
  meeting: AlertMeeting;
  participant: AlertParticipant | null;
};

export type AlertsList = {
  count: number;
  items: Array<AlertItem>;
  next_page: string | null;
  previous_page: string | null;
};

export const ALERT_STATE = {
  OPEN: "Open",
  ASSIGNED: "Assigned",
  RESOLVED: "Resolved",
  IGNORED: "Ignored",
} as const;

export type UpdateAlertPayload = {
  state: (typeof ALERT_STATE)[keyof typeof ALERT_STATE];
  assigned_to?: string;
  resolution_note?: string;
};

export type AlertDetail = {
  id: string;
  utterance_id: string;
  alert_types: Array<
    "pii_exposure" | "voice_authentication_failure" | "ai_content_detected"
  >;
  meeting_name: string;
  participant_full_name: string | null;
  priority: string;
  state: string;
  state_updated_at: string | null;
  pii_entities: Array<PiiEntityInAlert> | null;
  ai_content_score: AiContentScore | null;
  voice_auth_score: VoiceAuthScore | null;
  detected_at: string;
  utterance_text: string;
  resolution_note: string | null;
  assigned_to: string | null;
  assigned_by: string | null;
  created_at: string;
};

export function alertsListQueryOptions(params: AlertsListParams = {}) {
  const { page = 1, per_page = 10 } = params;

  return queryOptions({
    queryKey: ["alerts", "list", params],
    queryFn() {
      const searchParams = new URLSearchParams({
        page: page.toString(),
        per_page: per_page.toString(),
      });

      if (params.state) searchParams.set("state", params.state);
      if (params.alert_type) searchParams.set("alert_type", params.alert_type);
      if (params.search) searchParams.set("search", params.search);
      if (params.start_date) searchParams.set("start_date", params.start_date);
      if (params.end_date) searchParams.set("end_date", params.end_date);

      return api.processing.raw
        .get(`api/v1/alerts/?${searchParams.toString()}`)
        .json<AlertsList>();
    },
  });
}
